// Конвейер вставки и перетаскивания: собирает PasteData и по очереди (priority по убыванию) спрашивает
// обработчики registry.paste. Первый, чей match() = true и handle() вернул true/промис, забирает вставку;
// иначе работает стандартная вставка ProseMirror (HTML разбирается по parseHTML схемы, лишнее отбрасывается).
// Перед стандартным разбором HTML проходит через registry.htmlTransforms (очистка Word/Excel).
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Fragment, Slice, type Node as PMNode } from '@tiptap/pm/model';
import { registry, type PasteData } from '../registry';
import { ctx } from '../ctx';
import { USE_NBSP, plainSpaces, plainSpacesDeep } from '../schema/constants';

export const pasteKey = new PluginKey('stoPaste');

/** Вставляемый фрагмент без неразрывных пробелов и дефисов (пока USE_NBSP выключен) - и для малых редакторов. */
export function plainSlice(slice: Slice): Slice {
  if (USE_NBSP) return slice;
  const c = plainFragment(slice.content);
  return c === slice.content ? slice : new Slice(c, slice.openStart, slice.openEnd);
}

/** Фрагмент без неразрывных пробелов: текст и строковые атрибуты (код листинга, LaTeX, сноски). Тот же, если нечего менять. */
export function plainFragment(frag: Fragment): Fragment {
  let changed = false;
  const out: PMNode[] = [];
  frag.forEach((n) => {
    if (n.isText) {
      const t = plainSpaces(n.text || '');
      if (t !== n.text) { changed = true; out.push(n.type.schema.text(t, n.marks)); } else out.push(n);
      return;
    }
    const content = plainFragment(n.content);
    const attrs = plainSpacesDeep(n.attrs);
    if (content !== n.content || attrs !== n.attrs) { changed = true; out.push(n.type.create(attrs, content, n.marks)); } else out.push(n);
  });
  return changed ? Fragment.fromArray(out) : frag;
}

function collect(kind: 'paste' | 'drop', event: ClipboardEvent | DragEvent, dt: DataTransfer | null): PasteData {
  const files: File[] = [];
  if (dt) {
    for (const f of Array.from(dt.files || [])) files.push(f);
    if (!files.length) {
      for (const it of Array.from(dt.items || [])) {
        if (it.kind === 'file') { const f = it.getAsFile(); if (f) files.push(f); }
      }
    }
  }
  return {
    kind, event, files,
    html: dt?.getData('text/html') || '',
    text: dt?.getData('text/plain') || '',
    rtf: dt?.getData('text/rtf') || '',
    types: dt ? Array.from(dt.types || []) : [],
    slice: null, pos: null, moved: false,
  };
}

function dispatch(data: PasteData): boolean {
  for (const h of [...registry.paste.list()].sort((a, b) => b.priority - a.priority)) {
    let ok = false;
    try { ok = h.match(data, ctx); } catch (e) { console.error('paste.match', h.id, e); }
    if (!ok) continue;
    try {
      const r = h.handle(data, ctx);
      if (r && typeof (r as Promise<unknown>).then === 'function') {
        (r as Promise<unknown>).catch((e) => ctx.toast('Вставка: ' + (e?.message || e), 'error', 6000));
        return true;
      }
      if (r) return true;
    } catch (e: any) {
      console.error('paste.handle', h.id, e);
      ctx.toast('Вставка: ' + (e?.message || e), 'error', 6000);
      return true;
    }
  }
  return false;
}

export const PasteExtension = Extension.create({
  name: 'stoPaste',
  priority: 1000,
  addProseMirrorPlugins() {
    return [new Plugin({
      key: pasteKey,
      props: {
        handlePaste: (_view, event, slice) => {
          const data = collect('paste', event, event.clipboardData);
          data.slice = slice;
          return dispatch(data);
        },
        handleDrop: (view, event, slice, moved) => {
          if (moved) return false;                               // перенос внутри документа - стандартно
          const data = collect('drop', event, event.dataTransfer);
          data.slice = slice;
          data.moved = moved;
          data.pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? null;
          return dispatch(data);
        },
        // стандартная вставка (без обработчика): неразрывные пробелы и дефисы - обычными, пока USE_NBSP выключен
        transformPasted: plainSlice,
        transformPastedHTML: (html) => {
          let out = html;
          for (const t of registry.htmlTransforms.list()) {
            try { out = t.transform(out, ctx); } catch (e) { console.error('htmlTransform', t.id, e); }
          }
          return out;
        },
      },
    })];
  },
});
